import React, { useState, useEffect, useMemo } from "react";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { agentIntakeSchema } from "./formSchema";
import { uiSchema } from "./uiSchema";
import { buildUiSchemaWithShrunkLabels } from "../utils/uiSchemaHelper";
import PhoneWidget from "../widgets/PhoneWidget";
import TaxIdWidget from "../widgets/TaxIdWidget";
import NumericWidget from "../widgets/NumericWidget";
import ObjectFieldTemplate from "../templates/ObjectFieldTemplate";
import TitleFieldTemplate from "../templates/TitleFieldTemplate";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  ThemeProvider,
  createTheme,
  CssBaseline,
  CircularProgress,
} from "@mui/material";
import { IChangeEvent } from "@rjsf/core";
import { RJSFSchema } from "@rjsf/utils";
import styles from "../form.module.css";
import img from "../assets/657507_tmpl_head_60db3e2024ea6.png";
import { ExistingAgentIntakeFormData, NewAgentIntakeFormData } from "./agentIntakeModels";
import { convertJSONToMultiPartFormData } from "../utils/general";
import { host } from "config";
import FormSubmittedSplashPage from "../formSubmittedSplashPage";
import { useParams } from "react-router-dom";
import SaveAndResumeDialog from "../saveAndResume/saveAndResumeDialog";
import ConfirmResumeOTCDialog from "../saveAndResume/confirmResumeOTC";

const formNumber = "4";

const fileFields = [
  "updateAgentLicenseDocument",
  "updateAgencyLicenseDocument",
  "updateEODocument",
  "backgroundQ1Docs",
  "backgroundQ2Docs",
  "backgroundQ3Docs",
  "backgroundQ4Docs",
  "backgroundQ5Docs",
  "backgroundQ6Docs",
  "backgroundQ7Docs",
  "agentLicenseDocument",
  "agencyLicenseDocument",
  "eoDocument",
];

const sensitiveKeys = [
  "updateAgentLicenseDocument",
  "updateAgencyLicenseDocument",
  "updateEODocument",
  "backgroundQ1Docs",
  "backgroundQ2Docs",
  "backgroundQ3Docs",
  "backgroundQ4Docs",
  "backgroundQ5Docs",
  "backgroundQ6Docs",
  "backgroundQ7Docs",
  "agentLicenseDocument",
  "agencyLicenseDocument",
  "eoDocument",
  "agentSSN",
];

// Create MUI theme matching the form style
const theme = createTheme({
  palette: {
    primary: {
      main: "#2196f3",
    },
    background: {
      default: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Open Sans", Arial, sans-serif',
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
        InputLabelProps: {
          shrink: true,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          alignItems: "flex-start",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          marginLeft: 0,
          width: "100%",
          marginTop: 0,
          marginBottom: 0,
        },
        label: {
          marginLeft: "8px", // spacing between radio and text
          textAlign: "left",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 300,
              maxWidth: 300,
            },
          },
        },
      },
      styleOverrides: {
        select: {
          width: "700px",
          boxSizing: "border-box",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          alignItems: "flex-start",
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
  },
});

// Custom widgets
const widgets = {
  PhoneWidget: PhoneWidget,
  TaxIdWidget: TaxIdWidget,
  NumericWidget: NumericWidget,
};

interface FormData {
  agentType: any;
  updateType: any;
  isLicensedAgent: any;
  hasDBA: any;
  isAgencyOwner: any;
  commissionsToAgency: any;
  backgroundQ1: any;
  backgroundQ2: any;
  backgroundQ3: any;
  backgroundQ4: any;
  backgroundQ5: any;
  backgroundQ6: any;
  backgroundQ7: any;
}

// Custom field for PDB consent text
const PdbConsentField = ({ schema }) => {
  return (
    <Box
      sx={{
        textAlign: "center",
        fontSize: "1rem",
        color: "#333",
        my: 3,
        lineHeight: 1.6,
      }}
    >
      {schema.title}
    </Box>
  );
};

// Custom field for ACH instructions
const AchInstructionsField = () => {
  return (
    <Box
      sx={{
        textAlign: "center",
        fontSize: "1.5rem",
        fontWeight: 400,
        color: "#1a365d",
        my: 4,
        py: 3,
        px: 2,
        lineHeight: 1.8,
      }}
    >
      Please continue to the next page for the ACH form
      <br />
      and select Direct Deposit for Commissions
    </Box>
  );
};

// Custom fields
const fields = {
  pdbConsentField: PdbConsentField,
  achInstructionsField: AchInstructionsField,
};

// Custom templates
const templates = {
  ObjectFieldTemplate: ObjectFieldTemplate,
  TitleFieldTemplate: TitleFieldTemplate,
};

const AgentIntake: React.FC = () => {
  const [strippedFormData, setStrippedFormData] = useState({});
  const [formData, setFormData] = useState<ExistingAgentIntakeFormData | NewAgentIntakeFormData | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmittedPage, setShowSubmittedPage] = useState(false);
  const [openSaveAndResume, setOpenSaveAndResume] = useState(false);
  const [openConfirmResumeOTC, setOpenConfirmResumeOTC] = useState(false);

  const { guid } = useParams();

  useEffect(() => {
    if (guid && guid != "") {
      setOpenConfirmResumeOTC(true);
    }
  }, [guid]);

  // Dynamically create UI schema based on form data
  const dynamicUiSchema = useMemo(() => {
    const baseUiSchema = { ...uiSchema };
    if (!formData) {
      return buildUiSchemaWithShrunkLabels(agentIntakeSchema, baseUiSchema);
    }

    // Hide contact fields when agentType is selected
    if (formData.agentType === "existing" || formData.agentType === "new") {
      baseUiSchema.contactEmail = {
        "ui:widget": "hidden",
      };
      baseUiSchema.contactPhone = {
        "ui:widget": "hidden",
      };
      baseUiSchema.contactAgency = {
        "ui:widget": "hidden",
      };
    }

    // If existing agent, show/hide update fields based on selections
    if (formData.agentType === "existing" && formData.updateType) {
      const updateType = formData.updateType || [];

      // Agent License Update fields
      if (updateType.includes("agent_license")) {
        baseUiSchema.updateAgentLicenseNumber = {};
        baseUiSchema.updateStateOfAgentLicense = {};
        baseUiSchema.updateDateAgentLicenseEffective = { "ui:widget": "date" };
        baseUiSchema.updateDateAgentLicenseExpires = { "ui:widget": "date" };
        baseUiSchema.updateAgentLicenseDocument = {
          "ui:widget": "file",
          "ui:options": {
            accept: ".pdf,.jpg,.jpeg,.png",
          },
          "ui:help": "Accepted formats: PDF, PNG, JPG, JPEG.",
        };
      }

      // Agency License Update fields
      if (updateType.includes("agency_license")) {
        baseUiSchema.updateAgencyLicenseNumber = {};
        baseUiSchema.updateStateOfAgencyLicense = {};
        baseUiSchema.updateDateAgencyLicenseEffective = { "ui:widget": "date" };
        baseUiSchema.updateDateAgencyLicenseExpires = { "ui:widget": "date" };
        baseUiSchema.updateAgencyLicenseDocument = {
          "ui:widget": "file",
          "ui:options": {
            accept: ".pdf,.jpg,.jpeg,.png",
          },
          "ui:help": "Accepted formats: PDF, PNG, JPG, JPEG.",
        };
      }

      // E&O Update fields
      if (updateType.includes("eo")) {
        baseUiSchema.updateEOPolicyNumber = { "ui:widget": "NumericWidget" };
        baseUiSchema.updateDateEOEffective = { "ui:widget": "date" };
        baseUiSchema.updateDateEOExpires = { "ui:widget": "date" };
        baseUiSchema.updateEODocument = {
          "ui:widget": "file",
          "ui:options": {
            accept: ".pdf,.jpg,.jpeg,.png",
          },
          "ui:help": "I attest that I am an insured under the E&O certificate and/or policy attached",
        };
      }

      // New Carrier Appointment fields
      if (updateType.includes("new_carrier")) {
        baseUiSchema.newCarrierLicensedAgentName = {};
        baseUiSchema.newCarrierLicensedAgentNPN = { "ui:widget": "NumericWidget" };
      }
    }

    // For new agents, handle conditional fields
    if (formData.agentType === "new") {
      // Show/hide form filler fields (only show if NOT the licensed agent)
      if (formData.isLicensedAgent === "licensed" || !formData.isLicensedAgent) {
        baseUiSchema.fillerName = { "ui:widget": "hidden" };
        baseUiSchema.fillerEmail = { "ui:widget": "hidden" };
        baseUiSchema.fillerPhone = { "ui:widget": "hidden" };
      }

      // Show/hide DBA name field
      if (formData.hasDBA !== "yes") {
        baseUiSchema.dbaName = { "ui:widget": "hidden" };
      }

      // Show/hide agency owner fields (only show if agent is NOT the owner and answer is "no")
      if (formData.isAgencyOwner === "no") {
        // Show the fields - don't set them to hidden
        baseUiSchema.ownerFirstName = baseUiSchema.ownerFirstName || {};
        baseUiSchema.ownerLastName = baseUiSchema.ownerLastName || {};
        baseUiSchema.ownerEmail = baseUiSchema.ownerEmail || {};
      } else {
        // Hide the fields
        baseUiSchema.ownerFirstName = { "ui:widget": "hidden" };
        baseUiSchema.ownerLastName = { "ui:widget": "hidden" };
        baseUiSchema.ownerEmail = { "ui:widget": "hidden" };
      }

      // Show/hide agency information fields (only show if commissions payable to agency)
      if (formData.commissionsToAgency !== "yes") {
        baseUiSchema.agencyName = { "ui:widget": "hidden" };
        baseUiSchema.agencyNPN = { "ui:widget": "hidden" };
        baseUiSchema.hasDBA = { "ui:widget": "hidden" };
        baseUiSchema.dbaName = { "ui:widget": "hidden" };
        baseUiSchema.taxId = { "ui:widget": "hidden" };
        baseUiSchema.agencyAddress = { "ui:widget": "hidden" };
        baseUiSchema.agencyAddress2 = { "ui:widget": "hidden" };
        baseUiSchema.agencyCity = { "ui:widget": "hidden" };
        baseUiSchema.agencyState = { "ui:widget": "hidden" };
        baseUiSchema.agencyZip = { "ui:widget": "hidden" };
        baseUiSchema.operatingAs = { "ui:widget": "hidden" };
      }

      // Show/hide explanation fields for background questions
      if (formData.backgroundQ1 !== "yes") {
        baseUiSchema.backgroundQ1Explanation = { "ui:widget": "hidden" };
        baseUiSchema.backgroundQ1Docs = { "ui:widget": "hidden" };
      }
      if (formData.backgroundQ2 !== "yes") {
        baseUiSchema.backgroundQ2Explanation = { "ui:widget": "hidden" };
        baseUiSchema.backgroundQ2Docs = { "ui:widget": "hidden" };
      }
      if (formData.backgroundQ3 !== "yes") {
        baseUiSchema.backgroundQ3Explanation = { "ui:widget": "hidden" };
        baseUiSchema.backgroundQ3Docs = { "ui:widget": "hidden" };
      }
      if (formData.backgroundQ4 !== "yes") {
        baseUiSchema.backgroundQ4Explanation = { "ui:widget": "hidden" };
        baseUiSchema.backgroundQ4Docs = { "ui:widget": "hidden" };
      }
      if (formData.backgroundQ5 !== "yes") {
        baseUiSchema.backgroundQ5Explanation = { "ui:widget": "hidden" };
        baseUiSchema.backgroundQ5Docs = { "ui:widget": "hidden" };
      }
      if (formData.backgroundQ6 !== "yes") {
        baseUiSchema.backgroundQ6Explanation = { "ui:widget": "hidden" };
        baseUiSchema.backgroundQ6Docs = { "ui:widget": "hidden" };
      }
      if (formData.backgroundQ7 !== "yes") {
        baseUiSchema.backgroundQ7Explanation = { "ui:widget": "hidden" };
        baseUiSchema.backgroundQ7Docs = { "ui:widget": "hidden" };
      }
    }

    // Apply shrunk labels to all fields (forces labels to stay above inputs)
    return buildUiSchemaWithShrunkLabels(agentIntakeSchema, baseUiSchema);
  }, [formData]);

  const getHiddenFields = (dynamicUiSchema) => {
    return Object.keys(dynamicUiSchema).filter((key) => dynamicUiSchema[key]["ui:widget"] === "hidden");
  };

  const stripSensitiveFields = (formData: { [key: string]: any }) => {
    const strippedFormData = { ...formData };
    sensitiveKeys.map((key) => {
      delete strippedFormData[key];
    });
    setStrippedFormData(strippedFormData);
  };

  const handleOenSaveAndResume = () => {
    console.log(formData);
    stripSensitiveFields(formData);
    setOpenSaveAndResume(true);
  };

  const handleCloseSaveAndResume = () => {
    setOpenSaveAndResume(false);
  };

  const handleCloseConfirmResumeOTC = () => {
    setOpenConfirmResumeOTC(false);
  };

  const handleSubmit = (data: IChangeEvent<ExistingAgentIntakeFormData | NewAgentIntakeFormData>) => {
    const hiddenFields = getHiddenFields(dynamicUiSchema);
    const cleanedData = { ...formData };

    hiddenFields.forEach((field) => delete cleanedData[field]);

    setIsLoading(true);
    fetch(`${host}/api/implementation/forms/agentIntake`, {
      method: "POST",
      body: convertJSONToMultiPartFormData(cleanedData, fileFields),
    })
      .then((response) => {
        if (!response.ok) {
          response.text().then((text) => {
            throw new Error(text);
          });
        }
        response.json().then(async (data: any) => {
          setIsLoading(false);

          setShowSubmittedPage(true);
        });
      })
      .catch((error: any) => {
        console.error("Error:", error);
        alert(
          "There was an error while submitting the form. Please try again later and contact IT if the problem persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (data: IChangeEvent<ExistingAgentIntakeFormData | NewAgentIntakeFormData>) => {
    setFormData(data.formData);
  };

  const handleError = (errors) => {
    console.log("Form errors:", errors);
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  if (showSubmittedPage) {
    return <FormSubmittedSplashPage formName="Agent Intake" agentType={formData.agentType} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <img
              src={img}
              alt="OptiMed Health"
              style={{
                maxWidth: "350px",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          </Box>
          {!showForm ? (
            // Welcome Screen
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.8, maxWidth: 800, mx: "auto" }}
              >
                Thank you for taking the time to complete your agent/agency licensing online with OptiMedHealth! By
                completing the following online form, you will be asked to provide required documents to maintain an
                active appointment with OptiMedHealth and our Carriers. Please keep in mind that agent commissions are
                held until all required documents for an active appointment with OptiMedHealth has been received. If you
                have any questions or need support with this form, please contact us (800)-836-6036
              </Typography>
              <Box sx={{ mt: 6 }}>
                <Button
                  onClick={() => setShowForm(true)}
                  sx={{
                    backgroundColor: "#5a6c7d",
                    color: "white",
                    px: 3,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    minWidth: "150px",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#4a5c6d",
                      border: "none",
                    },
                  }}
                >
                  Start
                </Button>
              </Box>
            </Box>
          ) : (
            // Form View
            <>
              <Box
                sx={{
                  backgroundColor: "#1a365d",
                  color: "white",
                  py: 2.5,
                  px: 3,
                  mb: 3,
                  textAlign: "center",
                  fontSize: "28px",
                  fontWeight: 500,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontSize: "32px" }}>
                  Agent Data Intake
                </Typography>
              </Box>
              <Form
                schema={agentIntakeSchema}
                uiSchema={dynamicUiSchema}
                formData={formData}
                validator={validator}
                widgets={widgets}
                fields={fields}
                templates={templates}
                onChange={handleChange}
                onSubmit={handleSubmit}
                onError={handleError}
                showErrorList="top"
                liveValidate={false}
                className={styles["custom-form"]}
              />
              <Button
                variant="contained"
                onClick={() => handleOenSaveAndResume()}
                sx={{
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  minWidth: "150px",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                    border: "none",
                  },
                }}
              >
                Save and Resume Later
              </Button>
              <Box sx={{ mt: 3, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  * Required fields must be completed before submission
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Container>
      <SaveAndResumeDialog
        open={openSaveAndResume}
        onClose={handleCloseSaveAndResume}
        formNumber={formNumber}
        strippedFormData={strippedFormData}
      ></SaveAndResumeDialog>
      <ConfirmResumeOTCDialog
        open={openConfirmResumeOTC}
        onClose={handleCloseConfirmResumeOTC}
        guid={guid}
        setFormData={setFormData}
      />
    </ThemeProvider>
  );
};

export default AgentIntake;
